import React, { useCallback, useEffect, useRef } from 'react';
import useRootSelector from 'common/hooks/useRootSelector';
import { useNavigate } from 'react-router-dom';
import { Box } from '@material-ui/core';
import { LoadingBackdrop } from 'common/components';
import TokenService from 'services/auth/TokenService';
import {
  selectCurrentUserSelectedLevel,
  selectCurrentUserLoading,
  selectIsUserLoggedIn,
  selectIsExistCurrentUser,
  selectUserSelectedOrganization,
} from 'modules/authentication/state/selectors';
import { selectAlertsListLoading } from 'common/state/alerts/selectors';
import { fetchUserProfile, logOut } from 'modules/authentication/state/actions';
import { fetchAlertsList } from 'common/state/alerts/actions';
import { useAppDispatch } from 'store/hooks';
import useZendeskWidget from 'common/hooks/useZendeskWidget';
import { Routes } from 'constants/route.constants';
import { PermissionLevels } from 'common/constants/permissions';
import { fetchPeakSystemSettings, fetchProfileFieldsInfo } from 'common/state/settings/actions';
import {
  selectIsProfileFieldsInfoLoading,
  selectProfileFieldsInfo,
} from 'common/state/settings/selectors';

interface ICustomRouteProps {
  secure?: boolean;
  publicAccess?: boolean;
  children: React.ReactElement;
  levels: PermissionLevels[]; // Displays on which levels should we show the page
}

const CustomRoute = ({
  secure,
  levels,
  publicAccess,
  children,
}: ICustomRouteProps): JSX.Element => {
  const dispatch = useAppDispatch();
  const isInitialDataFetched = useRef<boolean>(false);
  const navigate = useNavigate();

  const isExistProfile = useRootSelector(selectIsExistCurrentUser);
  const isProfileLoading: boolean = useRootSelector(selectCurrentUserLoading());
  const isAlertsListLoading: boolean = useRootSelector(selectAlertsListLoading);
  const isUserLoggedIn: boolean = useRootSelector(selectIsUserLoggedIn);
  const userLevel: PermissionLevels = useRootSelector(selectCurrentUserSelectedLevel);
  const profileFieldsInfo = useRootSelector(selectProfileFieldsInfo);
  const isProfileFieldsInfoloading = useRootSelector(selectIsProfileFieldsInfoLoading);
  const selectedOrg = useRootSelector(selectUserSelectedOrganization);

  const isCorporateOrClubLevel =
    selectedOrg?.get('permissionsLevel') === PermissionLevels.CORPORATE ||
    selectedOrg?.get('permissionsLevel') === PermissionLevels.CLIENT;

  const routePreActions = useCallback(() => {
    const refreshToken = TokenService.getRefreshToken();
    const isRefreshExpired = TokenService.isTokenExpired(refreshToken);

    if (isRefreshExpired && isUserLoggedIn) {
      dispatch(logOut());
    }

    if (secure && !publicAccess && !isUserLoggedIn) {
      navigate(Routes.SIGN_IN);
      return;
    }

    if (
      !isInitialDataFetched.current &&
      secure &&
      isUserLoggedIn &&
      !isExistProfile &&
      !isRefreshExpired &&
      !isProfileLoading
    ) {
      isInitialDataFetched.current = true;
      dispatch(fetchUserProfile());
      dispatch(fetchAlertsList());
      dispatch(fetchPeakSystemSettings());
    }

    if (isCorporateOrClubLevel && !isProfileFieldsInfoloading && !profileFieldsInfo?.size) {
      dispatch(fetchProfileFieldsInfo());
    }

    if (secure && userLevel && levels && !levels.includes(userLevel)) {
      navigate(Routes.HOME);
    }
  }, [
    isUserLoggedIn,
    secure,
    isExistProfile,
    isProfileLoading,
    publicAccess,
    userLevel,
    levels,
    dispatch,
    navigate,
    profileFieldsInfo?.size,
    isProfileFieldsInfoloading,
    isCorporateOrClubLevel,
  ]);

  useEffect(() => {
    routePreActions();
  }, [routePreActions]);

  useEffect(() => {
    if (secure && !isUserLoggedIn && !publicAccess) {
      navigate(Routes.SIGN_IN);
    }

    if (levels && userLevel && !levels.includes(userLevel)) {
      navigate(Routes.HOME);
    }
  }, [isUserLoggedIn, levels, navigate, secure, userLevel, publicAccess]);

  useZendeskWidget();

  const canRenderRouter = !secure || (secure && isExistProfile) || publicAccess;

  return (
    <Box height="100%">
      {/*  {canRenderRouter && <Route path={path} element={element} />} */}
      {canRenderRouter && children}
      {(isProfileLoading || isAlertsListLoading) && <LoadingBackdrop isLoading />}
    </Box>
  );
};

export default CustomRoute;
